import { createContext, useContext, useSyncExternalStore } from "react";

// Tiny external store of the edits a form has not handed over yet (e.g. a
// conditions box with a debounced hand-off). It is an external store, and not
// state, so that only the components that read it (the Save button) re-render
// when it changes, not the form.
export function createPendingEditsStore() {
  const keys = new Set();
  const listeners = new Set();
  let pending = false;

  const update = () => {
    const next = keys.size > 0;
    if (next === pending) return;
    pending = next;
    listeners.forEach((listener) => listener());
  };

  return {
    begin(key) {
      keys.add(key);
      update();
    },
    end(key) {
      keys.delete(key);
      update();
    },
    isPending: () => pending,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}

export const PendingEditsContext = createContext(null);

export function usePendingEditsStore() {
  return useContext(PendingEditsContext);
}

const noopSubscribe = () => () => {};
const returnFalse = () => false;

// True while the form has an edit not yet handed over. False with no provider.
export function useHasPendingEdits() {
  const store = usePendingEditsStore();
  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    store ? store.isPending : returnFalse
  );
}
